import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import {
  PDivider,
  PIcon,
  PSpinner,
} from "@porsche-design-system/components-react";
import { useSwipeable } from "react-swipeable";
import { CSSTransition } from "react-transition-group";
import {
  dropShadowLowStyle,
  spacingFluidMedium,
  spacingFluidSmall,
  spacingFluidLarge,
  spacingFluidXSmall,
} from "@porsche-design-system/components-js/styles";
import {
  fadeInStyles,
  useGetTileBaseStyleDrop,
} from "MainApp/assets/visual/styles/globalStyles/globalJsxStyles";
import { IsRotatedContext } from "MainApp/assets/utils/context/isRotatedContext";
import { useTranslation } from "react-i18next";
import "./StickyDrawer.css";
import CButton from "Components/CComponents/CButton/CButton";
import CText from "Components/CComponents/CText/CText";
import {
  DEFAULT_CLOSED_DRAWER_CLEARANCE,
  getClosedDrawerClearance,
  getViewportAwareClosedDrawerClearanceValue,
} from "./stickyDrawerLayout";

const MIN_DRAWER_PEEK_HEIGHT = 88;
const CLOSED_DRAWER_CLEARANCE_CSS_VAR = "--sticky-drawer-closed-clearance";

const isEditableTarget = (target) =>
  Boolean(
    target?.matches?.(
      'input, textarea, select, [contenteditable="true"], [role="combobox"]'
    )
  );

const StickyDrawer = ({
  children,
  price,
  currency,
  isProcessingPayment,
  spinnerText,
  text,
  isPurchaseDisabled,
  isOpen: controlledIsOpen,
  onChangeOpenState,
}) => {
  const { t } = useTranslation("stickyDrawer");
  const { isRotated } = useContext(IsRotatedContext);
  const portalRoot = document.getElementById("portal-root");
  const [internalOpen, setInternalOpen] = useState(Boolean(controlledIsOpen));
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [focusedEditable, setFocusedEditable] = useState(false);
  const [viewportBottomOffset, setViewportBottomOffset] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== "undefined"
      ? Math.round(window.visualViewport?.height ?? window.innerHeight)
      : 0
  );
  const [closedDrawerBaseClearance, setClosedDrawerBaseClearance] = useState(
    null
  );
  const drawerFrameRef = useRef(null);

  const tileBasedStyle = useGetTileBaseStyleDrop();
  const isControlled = typeof controlledIsOpen === "boolean";
  const isOpen = isControlled ? controlledIsOpen : internalOpen;

  const setOpenState = useCallback((nextValue) => {
    const resolvedValue =
      typeof nextValue === "function"
        ? nextValue(isControlled ? controlledIsOpen : internalOpen)
        : nextValue;

    if (!isControlled) {
      setInternalOpen(resolvedValue);
    }
    onChangeOpenState?.(resolvedValue);
  }, [controlledIsOpen, internalOpen, isControlled, onChangeOpenState]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const threshold = 120;
    const handleViewportChange = () => {
      const visualViewport = window.visualViewport;
      const nextViewportHeight = Math.round(
        visualViewport?.height ?? window.innerHeight
      );
      const layoutHeight = Math.round(window.innerHeight);
      const visualOffsetTop = Math.round(visualViewport?.offsetTop ?? 0);
      const nextViewportBottomOffset = Math.max(
        0,
        layoutHeight - nextViewportHeight - visualOffsetTop
      );
      let embeddedViewportBottomOffset = 0;

      try {
        if (window.parent && window.parent !== window && window.frameElement) {
          const frameRect = window.frameElement.getBoundingClientRect();
          const parentVisualViewport = window.parent.visualViewport;
          const parentVisibleBottom =
            Math.round(parentVisualViewport?.offsetTop ?? 0) +
            Math.round(parentVisualViewport?.height ?? window.parent.innerHeight);

          embeddedViewportBottomOffset = Math.max(
            0,
            Math.ceil(frameRect.bottom - parentVisibleBottom)
          );
        }
      } catch (error) {
        embeddedViewportBottomOffset = 0;
      }

      const isKbOpen = layoutHeight - nextViewportHeight > threshold;
      setViewportHeight(nextViewportHeight);
      setViewportBottomOffset(
        Math.max(nextViewportBottomOffset, embeddedViewportBottomOffset)
      );
      setKeyboardOpen(isKbOpen);
    };

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, { passive: true });
    window.addEventListener("orientationchange", handleViewportChange);
    window.visualViewport?.addEventListener("resize", handleViewportChange);
    window.visualViewport?.addEventListener("scroll", handleViewportChange);

    try {
      if (window.parent && window.parent !== window) {
        window.parent.addEventListener("resize", handleViewportChange);
        window.parent.addEventListener("scroll", handleViewportChange, {
          passive: true,
        });
        window.parent.visualViewport?.addEventListener(
          "resize",
          handleViewportChange
        );
        window.parent.visualViewport?.addEventListener(
          "scroll",
          handleViewportChange
        );
      }
    } catch (error) {
      // Cross-origin embeds cannot expose parent viewport metrics.
    }

    handleViewportChange();

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange);
      window.removeEventListener("orientationchange", handleViewportChange);
      window.visualViewport?.removeEventListener("resize", handleViewportChange);
      window.visualViewport?.removeEventListener("scroll", handleViewportChange);
      try {
        if (window.parent && window.parent !== window) {
          window.parent.removeEventListener("resize", handleViewportChange);
          window.parent.removeEventListener("scroll", handleViewportChange);
          window.parent.visualViewport?.removeEventListener(
            "resize",
            handleViewportChange
          );
          window.parent.visualViewport?.removeEventListener(
            "scroll",
            handleViewportChange
          );
        }
      } catch (error) {
        // Cross-origin embeds cannot expose parent viewport metrics.
      }
    };
  }, []);

  useEffect(() => {
    const handleFocusIn = (event) => {
      if (isEditableTarget(event.target)) {
        setFocusedEditable(true);
      }
    };

    const handleFocusOut = () => {
      window.setTimeout(() => {
        setFocusedEditable(isEditableTarget(document.activeElement));
      }, 0);
    };

    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);

    return () => {
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
    };
  }, []);

  useEffect(() => {
    if (!isControlled) return undefined;
    setInternalOpen(Boolean(controlledIsOpen));
    return undefined;
  }, [controlledIsOpen, isControlled]);

  useEffect(() => {
    if (isOpen) {
      window.history.pushState({ drawerOpen: true }, "", window.location.href);
    }
    const handlePopState = () => {
      if (isOpen) {
        setOpenState(false);
        window.history.pushState(
          { drawerClosed: true },
          "",
          window.location.href
        );
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isOpen, setOpenState]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const updateClosedDrawerClearance = useCallback(() => {
    if (isOpen) return;

    const node = drawerFrameRef.current;
    if (!node) return;

    const nextClearance = getClosedDrawerClearance(
      node.getBoundingClientRect().height
    );
    setClosedDrawerBaseClearance((currentClearance) =>
      currentClearance === nextClearance ? currentClearance : nextClearance
    );
  }, [isOpen]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const baseClearance =
      closedDrawerBaseClearance ?? DEFAULT_CLOSED_DRAWER_CLEARANCE;

    document.documentElement.style.setProperty(
      CLOSED_DRAWER_CLEARANCE_CSS_VAR,
      getViewportAwareClosedDrawerClearanceValue(
        baseClearance,
        viewportBottomOffset
      )
    );

    return () => {
      document.documentElement.style.removeProperty(
        CLOSED_DRAWER_CLEARANCE_CSS_VAR
      );
    };
  }, [closedDrawerBaseClearance, viewportBottomOffset]);

  useEffect(() => {
    if (typeof window === "undefined" || isOpen) return undefined;

    const node = drawerFrameRef.current;
    if (!node) return undefined;

    updateClosedDrawerClearance();

    const handleResize = () => updateClosedDrawerClearance();

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(handleResize);
      observer.observe(node);
    }

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    window.visualViewport?.addEventListener("resize", handleResize);
    window.visualViewport?.addEventListener("scroll", handleResize);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      window.visualViewport?.removeEventListener("resize", handleResize);
      window.visualViewport?.removeEventListener("scroll", handleResize);
    };
  }, [isOpen, updateClosedDrawerClearance]);

  const toggleDrawer = () => {
    if (!isProcessingPayment) {
      setOpenState((open) => !open);
      if (isOpen) {
        window.history.pushState(
          { drawerClosed: true },
          "",
          window.location.href
        );
      }
    }
  };

  const handlers = useSwipeable({
    onSwipedUp: () => !isProcessingPayment && setOpenState(true),
    onSwipedDown: () => !isProcessingPayment && setOpenState(false),
    preventScrollOnSwipe: true,
    trackTouch: true,
    trackMouse: false,
  });

  if (!portalRoot) return null;

  const safeViewportHeight = Math.max(
    240,
    (viewportHeight || window.innerHeight || 0) - viewportBottomOffset
  );
  const minimumDrawerHeight = MIN_DRAWER_PEEK_HEIGHT + 24;
  const maximumDrawerHeight = Math.max(MIN_DRAWER_PEEK_HEIGHT, safeViewportHeight - 8);
  const preferredDrawerHeight = Math.round(safeViewportHeight * (keyboardOpen ? 0.9 : 0.82));
  const drawerHeight = Math.min(
    maximumDrawerHeight,
    Math.max(minimumDrawerHeight, preferredDrawerHeight)
  );
  const drawerHeightStyle = `min(${drawerHeight}px, calc(100dvh - 8px))`;
  const viewportAwareBottomOffset = `max(${viewportBottomOffset}px, calc(100vh - 100dvh))`;
  const shouldHideClosedDrawerForInput =
    !isOpen && (keyboardOpen || focusedEditable);

  return ReactDOM.createPortal(
    <div
      className={`liberty ${isOpen ? "open" : "closed"} ${
        shouldHideClosedDrawerForInput ? "input-active" : ""
      }`}
      style={{ bottom: viewportAwareBottomOffset }}
      {...handlers}
    >
      {isOpen && !isProcessingPayment && (
        <div
          className="drawer-overlay"
          onClick={toggleDrawer}
          style={fadeInStyles}
        />
      )}
      <div
        className="stickyDrawerConfirmationScreen"
        ref={drawerFrameRef}
        style={{
          gap: isOpen ? spacingFluidMedium : 0,
          padding: isOpen
            ? `${spacingFluidSmall} ${spacingFluidSmall} calc(${spacingFluidLarge} + env(safe-area-inset-bottom, 0px)) ${spacingFluidSmall}`
            : 0,
          transform: "translateY(0)",
          height: isOpen ? drawerHeightStyle : "auto",
          maxHeight: isOpen ? drawerHeightStyle : "none",
          overflow: isOpen ? "hidden" : "visible",
          "--drawer-peek-height": `${MIN_DRAWER_PEEK_HEIGHT}px`,
          "--drawer-content-max-height": drawerHeightStyle,
        }}
      >
        <PDivider className="dividerStickyDrawerConfirmationScreen" />
        <CSSTransition
          in={!isOpen}
          timeout={{ enter: 300, exit: 100 }}
          classNames="fade-handle"
          unmountOnExit
        >
          <div
            className="handle"
            onKeyUp={!isProcessingPayment ? toggleDrawer : null}
            onClick={toggleDrawer}
            style={{
              minHeight: isRotated ? "4rem" : "4.25rem",
              padding: `${spacingFluidXSmall} ${spacingFluidSmall} calc(${spacingFluidXSmall} + env(safe-area-inset-bottom, 0px))`,
            }}
          >
            <div className="slideUpIconContainerStickyDrawer">
              <PIcon
                name="arrow-head-up"
                aria={{ "aria-label": "Arrow Head Up icon" }}
              />
            </div>
            <div className="textsAreaStickyDrawer">
              <div
                className="totalTextContainerStickyDrawer"
                style={{ gap: spacingFluidXSmall }}
              >
                <CText size="large">{t("texts.total")}</CText>
                <CText size="large">
                  {price}
                  {currency}
                </CText>
              </div>
            </div>
            <div className="stickyDrawerAction">
              <CButton
                disabled={isProcessingPayment || isPurchaseDisabled}
              >
                {t("purchaseButton")}
              </CButton>
            </div>
          </div>
        </CSSTransition>
        <CSSTransition
          in={isOpen}
          timeout={{ enter: 300, exit: 100 }}
          classNames="drawer-content"
          unmountOnExit
        >
          <div
            className="drawer-content"
            style={{
              padding: `0 ${spacingFluidMedium} calc(${spacingFluidMedium} + env(safe-area-inset-bottom, 0px))`,
            }}
          >
            <div className="slideDownIconContainerStickyDrawer">
              <PIcon
                name="arrow-head-down"
                aria={{ "aria-label": "Arrow Head Down icon" }}
              />
            </div>
            {children}
          </div>
        </CSSTransition>
      </div>
      {isProcessingPayment && (
        <div className="spinnerOverlayOnStickyDrawer">
          <div
            className="spinnerContainer spinnerContainerOnStickyDrawer"
            style={{
              ...tileBasedStyle,
              ...dropShadowLowStyle,
              padding: spacingFluidXSmall,
            }}
          >
            <PSpinner />
            <CText>{spinnerText}</CText>
          </div>
        </div>
      )}
    </div>,
    portalRoot
  );
};

export default StickyDrawer;
